import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

import { cn } from '~/lib/cn';

// 'railway' variant is reserved for EN 45545 / rolling-stock contexts. Never use for general emphasis.
const cardVariants = cva(
  // Base: deep panel bg, subtle border, rounded, padded
  [
    'bg-[var(--color-deep-panel)]',
    'border border-[rgba(232,236,244,0.05)]',
    'rounded-sm',
    'p-6 md:p-8',
  ],
  {
    variants: {
      variant: {
        default: [],
        // Interactive: cyan glow on hover + pointer cursor
        interactive: [
          'cursor-pointer transition-all duration-200',
          'hover:border-[var(--color-cyan-signal)]/40 hover:border-[var(--color-cyan-signal)]',
          'hover:shadow-[0_0_24px_-8px_rgba(0,200,255,0.35)]',
        ],
        // Railway: rail-orange hover — RESERVED for EN 45545 / rolling-stock contexts only.
        railway: [
          'cursor-pointer transition-all duration-200',
          'hover:border-[var(--color-rail-orange)]',
          'hover:shadow-[0_0_24px_-8px_rgba(255,107,43,0.35)]',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type CardVariant = 'default' | 'interactive' | 'railway';
type CardElement = keyof React.JSX.IntrinsicElements;

interface CardProps extends VariantProps<typeof cardVariants> {
  variant?: CardVariant;
  as?: CardElement;
  children: React.ReactNode;
  className?: string;
  [key: string]: unknown;
}

function Card({ variant = 'default', as: Tag = 'div', children, className, ...rest }: CardProps) {
  return (
    <Tag className={cn(cardVariants({ variant }), className)} {...rest}>
      {children}
    </Tag>
  );
}

export { Card, cardVariants };
export type { CardProps, CardVariant };
export default Card;
